import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { usePmxPricesAndDatesActions } from "tap/store/pmxPricesAndDates/usePmxPricesAndDates.js";
import { tapApi } from "tap/api.js";
import { filterCriteria } from "tap/utils/filterCriteria.js";

// Minimum time to show the loading spinner (in ms)
const MIN_LOADING_DURATION = 400;

/**
 * Custom hook that consolidates PMX Prices and Dates data fetching logic.
 * Handles loading states, error toasts, and Redux updates.
 *
 * @returns {Object} Object containing fetchPMXPricesAndDates function and state values
 */
export function usePMXPricesAndDatesData() {
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { updatePMXPricesAndDates, clearPMXPricesAndDates } =
    usePmxPricesAndDatesActions();

  const {
    filterForm,
    pmxPricesAndDatesRange,
    pmxPricesAndDatesSort,
    itemsPerPage,
    defaultFilterRange,
  } = useSelector((s) => s.pmxPricesAndDates);

  /**
   * Fetches PMX Prices and Dates from the API with the given range.
   * Ensures loading spinner is visible for at least MIN_LOADING_DURATION.
   * @param {string} range - The pagination range (e.g., "0,25")
   * @param {Object|null} customFilterData - Optional filter data to use instead of Redux filterForm
   * @param {Array|null} customSort - Optional sort array to use instead of Redux pmxPricesAndDatesSort
   * @returns {Promise<Object|null>} The API response data or null on error
   */
  const fetchPMXPricesAndDates = useCallback(
    async (range, customFilterData = null, customSort = null) => {
      const startTime = Date.now();
      setLoadingTrue();

      const filterData = customFilterData ?? filterForm;
      const sortData = customSort ?? pmxPricesAndDatesSort;

      try {
        const data = await tapApi.pmxPricesAndDates(
          filterCriteria(filterData, "", "pmxPricesAndDates"),
          range,
          sortData,
        );

        if (data?.code || data?.httpCode || data?.error) {
          showErrorToast(
            data?.message ||
              data?.error?.message ||
              "Failed to fetch PMX prices and dates data.",
          );
          return null;
        }

        return data;
      } catch (error) {
        showErrorToast(
          error?.message || "Failed to fetch PMX prices and dates data.",
        );
        console.error("Error fetching PMX prices and dates:", error);
        return null;
      } finally {
        // Ensure minimum loading duration for consistent UX
        const elapsed = Date.now() - startTime;
        const remainingTime = MIN_LOADING_DURATION - elapsed;

        if (remainingTime > 0) {
          setTimeout(() => setLoadingFalse(), remainingTime);
        } else {
          setLoadingFalse();
        }
      }
    },
    [
      filterForm,
      pmxPricesAndDatesSort,
      setLoadingTrue,
      setLoadingFalse,
      showErrorToast,
    ],
  );

  return {
    fetchPMXPricesAndDates,
    updatePMXPricesAndDates,
    clearPMXPricesAndDates,
    itemsPerPage,
    pmxPricesAndDatesRange,
    pmxPricesAndDatesSort,
    filterForm,
    defaultFilterRange,
  };
}
